import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import log from "@app/util/log";

const EXPECTED_SILENT_AUTH_ERRORS = [
	"login_required",
	"consent_required",
	"interaction_required",
	"missing_refresh_token",
];

export default function useLogin() {
	const navigate = useNavigate();
	const [token, setToken] = useState(null);
	const [isBootstrapping, setIsBootstrapping] = useState(true);
	const {
		isLoading: auth0Loading,
		isAuthenticated,
		error,
		loginWithRedirect,
		getIdTokenClaims,
		getAccessTokenSilently,
	} = useAuth0();

	useEffect(() => {
		let isCancelled = false;

		const initAuth0 = async () => {
			if (auth0Loading) {
				setIsBootstrapping(true);
				return;
			}
			if (error) {
				setIsBootstrapping(false);
				return;
			}

			try {
				if (!isAuthenticated) {
					try {
						const accessToken = await getAccessTokenSilently();
						if (!accessToken) {
							throw new Error("No access token");
						}
						if (isCancelled) return;
						setToken(accessToken);
						navigate(0); // reload after token fetch
						return;
					} catch (silentAuthError) {
						if (
							EXPECTED_SILENT_AUTH_ERRORS.includes(
								silentAuthError?.error
							)
						) {
							log.debug("Silent authentication unavailable", {
								stage: "getAccessTokenSilently",
								error: silentAuthError.error,
							});
						} else {
							log.error(silentAuthError, {
								stage: "getAccessTokenSilently",
							});
						}
						localStorage.setItem(
							"returnTo",
							window.location.toString()
						);
						await loginWithRedirect({
							appState: { returnTo: window.location.toString() },
						});
						return;
					}
				}

				const claims = await getIdTokenClaims();
				if (isCancelled) return;

				if (claims?.["__raw"]) {
					setToken(claims["__raw"]);
				} else {
					localStorage.setItem(
						"returnTo",
						window.location.toString()
					);
					await loginWithRedirect({
						appState: { returnTo: window.location.toString() },
					});
					return;
				}
			} finally {
				if (!isCancelled) {
					setIsBootstrapping(false);
				}
			}
		};

		initAuth0();

		return () => {
			isCancelled = true;
		};
	}, [
		auth0Loading,
		error,
		getAccessTokenSilently,
		getIdTokenClaims,
		isAuthenticated,
		loginWithRedirect,
		navigate,
	]);

	return {
		isAuthenticated,
		isLoading:
			auth0Loading ||
			isBootstrapping ||
			(isAuthenticated && !token && !error),
		error,
		token,
	};
}
